import { useCallback, useEffect, useState } from "react";

import instance from "../Interceptor/api_instance";
import { PaginatedResponse } from "../types/api";

interface UsePaginatedQueryOptions {
  initialUrl: string;
  params?: Record<string, any>;
}

export function usePaginatedQuery<T>({
  initialUrl,
  params,
}: UsePaginatedQueryOptions) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const [results, setResults] = useState<T[]>([]);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const [previousUrl, setPreviousUrl] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);

  const fetchPage = useCallback(
    async (url: string, pageDirection?: "next" | "previous" | "reset") => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await instance.get<PaginatedResponse<T>>(url, {
          params,
        });
        const data = response.data;

        setResults(data.results);
        setNextUrl(data.next);
        setPreviousUrl(data.previous);

        setCurrentPage((prevPage) => {
          if (pageDirection === "next") return prevPage + 1;
          if (pageDirection === "previous") return prevPage - 1;
          return 1;
        });
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    },
    [params],
  );

  const fetchNextPage = useCallback(() => {
    if (!nextUrl) return;
    fetchPage(nextUrl, "next");
  }, [nextUrl, fetchPage]);

  const fetchPreviousPage = useCallback(() => {
    if (!previousUrl) return;
    fetchPage(previousUrl, "previous");
  }, [previousUrl, fetchPage]);

  const refetch = useCallback(() => {
    fetchPage(initialUrl, "reset");
  }, [fetchPage, initialUrl]);

  useEffect(() => {
    fetchPage(initialUrl, "reset");
  }, [fetchPage, initialUrl]);

  return {
    results,
    hasNext: Boolean(nextUrl),
    hasPrevious: Boolean(previousUrl),
    currentPage,

    fetchNextPage,
    fetchPreviousPage,
    refetch,

    isLoading,
    error,
  };
}
